import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { ICompanyInfo } from 'common/Session';
import { Scenario as GqlScenario } from 'common/gql';
import { Group, IPublishInfo, IShareScenario, PermissionEnum, User } from 'common';
import { Img } from 'react-image';
import Panel from '../layout/Panel';
import BoolProperty from '../properties/BoolProperty';
import Dialog from '../layout/Dialog';
import ProjectPacker from '../../classes/ProjectPacker';
import Spacer from '../layout/Spacer';
import { ApplicationModuleTypes, ChecklistFormType, LayoutOrientations, PublishTypes, QuizType } from '../../globals';
import DropdownProperty from '../properties/DropdownProperty';
import Utils from '../../classes/Utilities';
import TextUtils from '../../classes/utilities/TextUtils';
import store, { StoreActions } from '../../state/store';
import { WarningTriangleIcon, IconFillStyles, InverseWarningTriangleIcon, Chevron, SearchIcon, Spinner, InfoIconRound, TriangleIconRight, ClearIcon } from '../Iconset';
import { useTypedSelector } from '../../state/hooks';
import ScenarioHealthUtils from '../../classes/utilities/ScenarioHealthUtils';
import { FeatureFlagNames, HealthReportType } from '../../interfaces/enums';
import { ProjectStates } from '../../interfaces/ProjectStates';
import Button from '../layout/Button';
import { openWindow } from '../../classes/ShortcutHandler';
import APIUtility from '../../classes/APIUtility';
import { IPublishSettings } from '../../interfaces/Misc';
import { IProjectInfo } from '../../interfaces/ScenarioElements';
import SearchField from '../layout/SearchField';
import { enumToDropdownOptions } from '../../classes/utilities/Misc';
import InitialsAvatar from '../applicationTopBar/InitialsAvatar';
import { TooltipKeys } from '../tooltips/TooltipKeys';
import Scenario from '../../classes/scenarioElements/Scenario';
import SessionUtils from '../../classes/utilities/SessionUtils';
import GQLuser from '../../classes/api/GQL/user/GQLuser';

function getOriginGroup(company?: ICompanyInfo, scenario?: GqlScenario)
{
	if (scenario?.originGroup)
	{
		return { uuid: scenario.originGroup.id, name: scenario.originGroup.name };
	}
	else 
	{
		return company?.user_origin_group || { name: 'no group', uuid: 'no group' };
	}
}

function doesProjectContainsQuiz()
{
	for (const form of Object.values(store.getState().checklistModule.checklist))
	{
		if ((form as any).type === ChecklistFormType.QUIZ)
		{
			return true;
		}
	}
}

function shoudShowSessionResetWarning(publishType: PublishTypes, quizSettings: any, publishInfo: IPublishInfo | null)
{
	return 	publishType === PublishTypes.persistent_per_user && 
			quizSettings.type === QuizType.GRADED && 
			publishInfo?.published_at;
}

function shouldShowViewerWarning(projectState: ProjectStates, hideViewerWarning?: boolean)
{
	return 	SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.RevisionManagement) && 
			projectState !== ProjectStates.Approved && 
			!hideViewerWarning;
}

enum UserGroupListFitlerTypes {
	AllUsers,
	Owners,
	Editors,
	Reviewers,
	Viewers,
}

interface PublishScenarioDialogProps
{
	onShutdown?: () => void;
	onPublish?: () => void;
	hideViewerWarning?: boolean;
}

export default function PublishScenarioDialog(props: PublishScenarioDialogProps)
{
	const [project, setProject] = useState<IProjectInfo | null>(null);
	const [recipientGroupIds, setRecipientGroupsIds] = useState<string[]>([]);
	const [publishType, setPublishType] = useState(doesProjectContainsQuiz() ? PublishTypes.persistent_per_user : PublishTypes.passive_session);
	const [encrypt, setEncrypt] = useState(false);
	const [openInInteractiveMode, setOpenInInteractiveMode] = useState(false);
	const [bypassCPS, setBypassCPS] = useState(false);
	const [groupUserSearchTerm, setGroupUserSearchTerm] = useState<string>("");
	const [searchFilterType, setSearchFilterType] = useState<UserGroupListFitlerTypes>(UserGroupListFitlerTypes.AllUsers);
	const [usersAndGroups, setUsersAndGroups] = useState<Array<User | Group>>([]);
	const [originalPermissables, setOriginalPermissables] = useState<Array<User | Group>>([]);
	const [showUserGroupList, setShowUserGroupList] = useState(false);
	const [permissions, setPermissions] = useState<IShareScenario["replaceResourcePermissions"]>([]);

	const quizSettings = useTypedSelector(state => state.checklistModule.quizSettings);
	const publishInfo = useTypedSelector(state => state.scenarioModule.scenarioPublishInfo);
	const publishSettings = useTypedSelector(state => state.applicationModule.publishSettings);
	const healthReports = useTypedSelector(state => state.scenarioModule.healthReports);

	const sessionInfo = useTypedSelector(state => state.userModule.sessionInfo, shallowEqual);
	const company = sessionInfo?.companies[0];
	const scenario = project?.scenarios?.[0];
	const canUpdate  = store.getState().scenarioModule.permissions.canUpdate;
	const originGroup = getOriginGroup(company, scenario);
	const projectState = useTypedSelector(state => state.scenarioModule.projectState);

	const publishDescriptions = {
		[PublishTypes.demo_session]: "Users cannot resume this scenario and sessions are not tracked.",
		[PublishTypes.passive_session]: "Users cannot resume this scenario. Not available for graded quizzes.",
		[PublishTypes.persistent_for_any_user]: "Allows any user to resume this scenario. Not available for quizzes.",
		[PublishTypes.persistent_per_user]: "Allows each user to resume this scenario based on their own progress. Recommended for quizzes.",
	};

	const publishErrors = {
		[PublishTypes.passive_session]: "Not available for graded quizzes",
		[PublishTypes.persistent_for_any_user]: "Not available if any step has a quiz question",
		[PublishTypes.persistent_per_user]: "Not available if no step has a quiz question",
	};

	useEffect(() =>
	{
		if(openInInteractiveMode && bypassCPS)
		{
			setBypassCPS(false);
		}	
	}, [openInInteractiveMode]);

	useEffect(() =>
	{
		if(bypassCPS && openInInteractiveMode)
		{
			setOpenInInteractiveMode(false);
		}
	}, [bypassCPS]);

	useEffect(() =>
	{
		if(publishSettings)
		{
			if(publishSettings.publishType || publishSettings.publishType === 0)
			{
				setPublishType(publishSettings.publishType);
			}
			if(publishSettings.encrypt !== undefined)
			{
				setEncrypt(publishSettings.encrypt);
			}
			if(publishSettings.openInInteractiveMode !== undefined)
			{
				setOpenInInteractiveMode(publishSettings.openInInteractiveMode);
			}
			if(publishSettings.bypassCPS !== undefined)
			{
				setBypassCPS(publishSettings.bypassCPS);
			}
		}
	}, [publishSettings]);

	useEffect(() => 
	{
		async function fetchProject()
		{
			//console.log("Fetching project");
			const projectId = Scenario.current()?.id;
			if(!projectId)
			{
				console.error("No project ID found. Cannot fetch information!");
				return;
			}

			// actually fetch the project list from the CMS
			APIUtility.getProject(projectId).then((data: { project: IProjectInfo }) =>
			{
				//console.log("Project", data);
				setProject(data.project);
				setRecipientGroupsIds(data.project?.scenarios?.[0]?.recipientGroups?.nodes?.map((group) => group!.id) || []);
				
				if(data.project?.scenarios?.[0]?.resourcePermissions)
				{
					setOriginalPermissables(data.project.scenarios[0].resourcePermissions.map(permission => permission.permissable as User | Group));
					setPermissions(data.project.scenarios[0].resourcePermissions.map(permission => 
					{
						return {
							permission: permission.permission as PermissionEnum,
							permissableId: permission.permissable?.id,
							permissableType: permission.permissableType,
							permissableDatabaseId: permission.permissable?.databaseId,
						}
					}));
				}
			}).catch((error: any) => 
			{
				console.error("Error fetching project", error);
			});
		}

		async function fetchUsersAndGroups()
		{
			//console.log("Fetching all users and groups");

			// actually fetch the project list from the CMS 
			// using whitespace to fetch everything because we can't sent an empty string (returns error 400)
			GQLuser.searchUsersAndGroups(" ", SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.viewOnlyMode)).then((data: { usersAndGroups: Array<User | Group> }) =>
			{
				//console.log("Users and Groups", data);
				setUsersAndGroups(data.usersAndGroups);
			}).catch((error: any) =>
			{
				console.error("Error fetching users and groups", error);
			});
		}

		if (!project)
		{
			fetchProject();
		}

		fetchUsersAndGroups();
	}, []);

	const userGroupDisplayList = useMemo(() => 
	{
		// don't display the current user
		let displayList = usersAndGroups.filter(item => item.id !== sessionInfo?.cmsUserGqlId);

		// don't display the origin group
		displayList = displayList.filter(item => item.id !== originGroup.uuid);

		// don't display items filtered by search term
		if(groupUserSearchTerm)
		{
			displayList = displayList.filter(item => item.name && TextUtils.searchString(item.name, groupUserSearchTerm));
		}

		// don't display items that already have permissions
		displayList = displayList.filter(item => !permissions?.find(permission => permission.permissableId === item.id));

		// render the list
		const output = Array<React.ReactNode>();
		for(const item of displayList)
		{
			output.push(
				<UserGroupListItem key={item.id}
					item={item}
					onClick={(item) =>
					{
						const newPermissions = permissions ? [...permissions] : [];
						newPermissions.push({
							permission: 'READ' as any,
							permissableId: item.id,
							permissableType: item.__typename?.toUpperCase() as any,
							permissableDatabaseId: item.databaseId,
						});
						setPermissions(newPermissions);
						setGroupUserSearchTerm("");
						setShowUserGroupList(false);
					}}
				/>
			);
		}

		return output;
	}, [usersAndGroups, groupUserSearchTerm, permissions]);

	const permissionsList = useMemo(() =>
	{
		const output = Array<React.ReactNode>();

		if(permissions)
		{
			for(let index = 0; index < permissions.length; index++)
			{
				const permission = permissions[index];
				if(searchFilterType !== UserGroupListFitlerTypes.AllUsers)
				{
					if(searchFilterType === UserGroupListFitlerTypes.Owners && permission.permission !== 'MANAGE') continue;
					if(searchFilterType === UserGroupListFitlerTypes.Editors && permission.permission !== 'UPDATE') continue;
					if(searchFilterType === UserGroupListFitlerTypes.Reviewers && permission.permission !== 'REVIEW') continue;
					if(searchFilterType === UserGroupListFitlerTypes.Viewers && permission.permission !== 'READ') continue;
				}

				// keep track of the originalPermissables we got from the API, because the current author may not
				// have access to all of them via usersAndGroups search
				const itemData = originalPermissables.concat(usersAndGroups).find(item => item.id === permission.permissableId);
				if(itemData && itemData.id !== originGroup.uuid)
				{
					output.push(
						<UserGroupListItem key={index}
							className={"no-hover"}
							item={itemData}
							permission={permission.permission}
							onPermissionChange={(change) =>
							{
								const newPermissions = permissions ? [...permissions] : [];
								switch(change)
								{
									case PermissionChangeValues.Owner:
										newPermissions[index].permission = 'MANAGE' as any;
										break;
									case PermissionChangeValues.Editor:
										newPermissions[index].permission = 'UPDATE' as any;
										break;
									case PermissionChangeValues.Viewer:
										newPermissions[index].permission = 'READ' as any;
										break;
									case PermissionChangeValues.Remove:
										newPermissions.splice(index, 1);
										break;
								}

								setPermissions(newPermissions);
							}}
						/>
					);
				}
			}
		}

		return output;
	}, [permissions, usersAndGroups, searchFilterType]);

	const warningCounter = useMemo(() => 
	{
		return ScenarioHealthUtils.getReportsOfType(HealthReportType.Warning).length + " Warning" + (ScenarioHealthUtils.getReportsOfType(HealthReportType.Warning).length > 1 ? "s" : "");
	}, [healthReports]);

	return (
		<Dialog data-name={"scenario-publish-dialog-root"}
			className={"groupedButtons"}
			title={"Publish Scenario"}
			onShutdown={props.onShutdown}
			style={{
				width: '550px',
			}}
			actions={ScenarioHealthUtils.getReportsOfType(HealthReportType.Error).length > 0 ? [] : [
				{
					label: "Publish",
					disabled: !project || !Utils.isPublishTypeValid(publishType).valid,
					onClick: async () =>
					{
						const publishSettings: IPublishSettings = {
							publishType: publishType,
							encrypt: encrypt,
							openInInteractiveMode: openInInteractiveMode,
							bypassCPS: bypassCPS,
						};
						store.dispatch(StoreActions.application.setPublishSettings(publishSettings));
						APIUtility.saveWlcSettings();

						// WC20-5040: force save project before publishing
						await ProjectPacker.getInstance().saveProject();

						// WC20-2258: always include default group
						ProjectPacker.getInstance().exportProject(true, [...recipientGroupIds, originGroup.uuid], publishType, encrypt, openInInteractiveMode, bypassCPS, permissions);
						if(props.onPublish)
						{
							props.onPublish();
						}
					}
				},
				{
					label: "Cancel",
				}
			]}
			onClick={() => setShowUserGroupList(false)}
		>
			<div>
				Choose Session Type:
			</div>
			<Spacer size={{ y: "4px" }} />
			<DropdownProperty data-name={"property-scenario-type"}
				value={publishType}
				defaultValue={PublishTypes.passive_session}
				onChangeCallback={setPublishType}
				options={Object.entries(PublishTypes).filter(([oKey, oValue]) => typeof oValue === 'number').map((keyValue) =>
				{
					const [key, value] = keyValue;
					const validType = Utils.isPublishTypeValid(value).valid;
					return { 
						display: TextUtils.prettifyText(key.toString()) + (validType ? "" : publishErrors[value] !== undefined ? ` (${publishErrors[value]})` : ""), 
						value: value, 
						disabled: !validType,
					};
				})}
			/>
			{
				Utils.isPublishTypeValid(publishType).message &&
					<div style={{ maxWidth:'250px', color: !Utils.isPublishTypeValid(publishType).valid ? '#D84C4C' : '' }}>
						{Utils.isPublishTypeValid(publishType).message}
					</div>
			}
			<div className={"dimmedLow"}>
				{ publishDescriptions[publishType] }
				{
					shoudShowSessionResetWarning(publishType, quizSettings, publishInfo) &&
						<>
							<Spacer size={{ y: "16px"}} />
							<Panel orientation={LayoutOrientations.Horizontal} className={"centerContentHorizontally"}>
								<WarningTriangleIcon />
								<Spacer size={{ x: "8px"}} />
								Publishing a new version will clear resumable sessions for any session in progress.
							</Panel>
						</>
				}
			</div>

			<Spacer size={{ y: "8px" }} />

			<BoolProperty labelAfterProperty='Encrypt Scenario'
				key='encrpyt-bool'
				value={encrypt}
				onChangeCallback={(v) =>
				{
					setEncrypt(v);
				}}
			/>
			<BoolProperty labelAfterProperty='Open in interactive mode (mobile only)'
				key='interactive-only-bool'
				value={openInInteractiveMode}
				onChangeCallback={(v: boolean) =>
				{
					setOpenInInteractiveMode(v);
				}}
			/>
			{
				!SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.allowBypassCPS) ? null :
					<BoolProperty labelAfterProperty='Bypass content placement'
						key='bypass-cps-bool'
						value={bypassCPS}
						onChangeCallback={(v: boolean) =>
						{
							setBypassCPS(v);
						}}
					/>
			}
			
			<Spacer size={{ y: "8px" }} />
			<div>Publish to: <u className={"regLink "}>{originGroup?.name}</u></div>
			{canUpdate && 
			<>
				<Spacer size={{ y: "8px" }} />
				Share Published Scenario With:
				<Spacer size={{ y: "8px" }} />
				<div style={{ width: "502px" }}>
					<Panel data-name={"group-user-search-root"}
						className={"comboSearchField"}
					>
						{
							usersAndGroups.length > 0 ?
								<SearchIcon className={"dimmedLow"} />
								:
								<Spinner className={"dimmedLow"} size={24} />
						}
						<SearchField data-name={"group-user-search"}
							className={""}
							value={groupUserSearchTerm}
							placeholder={usersAndGroups.length ? "Search for Groups and Users" : "Fetching Groups and Users..."}
							readOnly={!usersAndGroups.length}
							onChangeCallback={setGroupUserSearchTerm}
							onClick={() => setShowUserGroupList(true)}
						/>
						<DropdownProperty data-name={"type-filter"}
							className={"fit"}
							value={searchFilterType}
							readOnly={!usersAndGroups.length}
							onChangeCallback={setSearchFilterType}
							options={enumToDropdownOptions(UserGroupListFitlerTypes)}
						/>
					</Panel>
					{
						showUserGroupList && userGroupDisplayList.length > 0 &&
						<div data-name={"user-group-list-root"}
							className={"userGroupListRoot"}
						>
							{userGroupDisplayList}
						</div>
					}
				</div>
			</>
			}
			{
				permissionsList.length > 0 &&
					<>
						<Spacer size={{ y: "8px" }} />
						<Panel data-name={"permission-list-root"}
							className={"permissionListRoot"}
						>
							<Panel data-name={"permission-list-header"}
								className={"permissionListHeader"}
							>
								<InfoIconRound scale={1.2} 
									data-tooltip={TooltipKeys.ScenarioPublishGroupsUsersInfo}
									className={"dimmedLow"}
								/>
								Groups / Authors
							</Panel>
							<Panel class={"permissionList"}>
								{ permissionsList }
							</Panel>
						</Panel>
					</>
			}

			{
				ScenarioHealthUtils.getReportsOfType(HealthReportType.Error).length > 0 ? 
					<Panel className={"solid composite publishGroupsWrapper centerContent"}>
						You cannot publish until all errors are resolved
						<Spacer size={{ y: "10px" }} />
						<Button
							category={"PublishProject"}
							label={"View Errors"}
							className={"viewErrorsButton"}
							onClick={()=>
							{
								if(props.onShutdown)
								{
									props.onShutdown();
								}
								openWindow(ApplicationModuleTypes.ProjectHealth);
							}}
						/>	
						<Spacer size={{ y: "10px" }} />
					</Panel>
					:
					ScenarioHealthUtils.getReportsOfType(HealthReportType.Warning).length > 0 ?
						<Panel 
							orientation={LayoutOrientations.Horizontal}
							className={"publishWarningsContainer"}
							onClick={()=>
							{
								if(props.onShutdown)
								{
									props.onShutdown();
								}
								openWindow(ApplicationModuleTypes.ProjectHealth);
							}}
						>
							<InverseWarningTriangleIcon className='yellowWarning' fillStyle={IconFillStyles.yellow}/>
							{warningCounter}
							<Chevron className='publishWarningsChevron' rotate={270} key={'Chevron'}/>
						</Panel>
						: null
			}

			{
				shouldShowViewerWarning(projectState, props.hideViewerWarning) &&
					<>
						<Spacer size={{ y: "10px" }} />
						<Panel 
							orientation={LayoutOrientations.Horizontal}
							className={"no-overflow"}
						>
							<InverseWarningTriangleIcon className='yellowWarning' fillStyle={IconFillStyles.red}/>
							<Spacer size={{ x: "12px" }} />
							You are publishing to a group with Viewers, but the project is not yet approved. Viewers will not be able to access the scenario until the project's status is changed to Approved
						</Panel>
					</>
			}
		</Dialog>
	)
}

enum PermissionChangeValues {
	Owner = "Owner",
	Editor = "Editor",
	Reviewer = "Reviewer",
	Viewer = "Viewer",
	Remove = "Remove",
}

interface IUserGroupListItemProps
{
	item: User | Group;
	className?: string;
	permission?: any;
	onClick?: (item: User | Group) => void;
	onPermissionChange?: (change: PermissionChangeValues) => void;
}

const UserGroupListItem = (props: IUserGroupListItemProps) =>
{
	const additionalInfo = props.item.__typename === "Group" ? `${props.item.members?.totalCount} members` : SessionUtils.getAuthorLicense(props.item as User);
	const defaultAvatar = 
		<InitialsAvatar name={props.item.name || ""}
			style={props.item.__typename === "Group" ? { backgroundColor: "#C98B16" } : undefined}
		/>;

	return (
		<Panel data-name={"group-user-list-item-" + props.item.id}
			className={["groupUserListItemRoot", props.className].join(' ')}
			orientation={LayoutOrientations.Horizontal}
			expand={0}
			onClick={() => props.onClick?.(props.item)}
		>
			<Img src={props.item.__typename === "User" ? props.item.avatar?.fileUrl : undefined}
				loader={defaultAvatar}
				unloader={defaultAvatar}
				className={"groupUserListItemAvatar"}
				height={32}
				width={32}
			/>
			<Panel className={"groupUserListItemInfo"}>
				{props.item.name}
				<div data-name={"additional-info"}
					className={"additionalInfo"}
				>
					{additionalInfo}
				</div>
			</Panel>
			{
				props.permission &&
				<>
					<Spacer stretchTo={{ x: "100%" }} />
					<ClearIcon data-name={"remove-access"} 
						scale={0.75}
						onClick={() => props.onPermissionChange?.(PermissionChangeValues.Remove)}
					/>
				</>
			}
		</Panel>
	);
};